/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useState } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Logo from '@comp/Logo/Logo';
import Divider from '@base/Divider/Divider';
import useTimeout from '@hooks/useTimeout';
import Image from '@base/Image';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Dynamic from '@base/Dynamic/Dynamic';
import { Button } from '@material-ui/core';
import useEducatorContext from '@hooks/useEducatorContext';
import { useFlagSelector } from '@hooks/useLayoutOptions';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { getIsLoggedIn } from '@api/selectors/auth';
import useWindowSize from '@hooks/useWindowSize';
import usePlatformInfo from '@hooks/usePlatformInfo';
import { getIsGTPGoal } from '@api/selectors/constants';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import usePrimeHomeStore from '@stores/prime-home';
import useSelectedGoal, {
  useSelectedGoalUID
} from '@api/hooks/topology/useSelectedGoal';
import useConstants from '@api/hooks/useConstants';
import CompeteHeaderStrip from '@cont/Compete/CompeteHeaderStrip';
import useCompeteConfig from '@cont/Compete/useCompeteConfig';
import { COMPETE_STATES } from '@cont/Compete/constants';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import {
  REFERRAL_TOPBAR_TOUCHPOINT,
  REFERRAL_TOUCHPOINT_CLICKED
} from '@constants/segment';
import HeaderContent from '../Components/HeaderParent';
import HeaderTop from '../Components/HeaderContainer';
import RenderLoginAction from './RenderLoginAction';
import UpscCurrentAffairsActions from './UpscCurrentAffairsActions';
import {
  isUpscCurrentAffairsRoute,
  UPSC_CURRENT_AFFAIRS_HEADER_TITLE
} from './upscCurrentAffairsHeaderUtils';

const GoalSelector = Dynamic(() => import('./GoalSelector/HeaderGoalSelector'));
const SearchTab = Dynamic(() => import('./SearchTab'));
const HeaderUser = Dynamic(() => import('./HeaderUser/HeaderUser'));
const ProfileOptions = Dynamic(() =>
  import('./HeaderUser/ProfileOptions/ProfileOptions')
);
const PaymentNudge = Dynamic(() => import('../PaymentLayout/PaymentNudge'));
const HeaderNotification = Dynamic(() => import('./HeaderNotification'));
const AutoDppDismissibleCard = Dynamic(() =>
  import('@educator/Composite/DashBoard/AutoDppDismissibleCard')
);
const NotificationToPwa = Dynamic(() => import('@layout/NotificationToPwa'));

const HeaderContainer = styled(HeaderTop)`
  background: ${({ transparent, hideBgColor, $primeTheme }) => {
    if ($primeTheme) return 'var(--color-i-prime-surface)';
    if (transparent || hideBgColor) return 'none';
    return 'var(--color-base-1)';
  }};
  box-shadow: ${({ transparent, hideBgColor, hideBoxShadow, $primeTheme }) =>
    $primeTheme || transparent || hideBgColor || hideBoxShadow
      ? 'initial'
      : boxShadowGenerator()};
  position: ${({ transparent }) => (transparent ? 'absolute' : 'sticky')};
  height: ${({ $isCompetePostLaunch }) =>
    $isCompetePostLaunch ? '136px' : Spacings.SPACING_18B};
  ${mediaQuery.uptoTablet} {
    height: ${({ $isCompetePostLaunch }) =>
      $isCompetePostLaunch ? '136px' : Spacings.SPACING_18B};
  }
  ${mediaQuery.uptoMobile} {
    box-shadow: ${({ $primeTheme }) =>
      $primeTheme ? 'none' : 'inset 0px -1px 0px var(--color-shadow)'};
    display: block;
    width: 100%;
    height: ${({ $isCompetePostLaunch }) =>
      $isCompetePostLaunch ? '198px' : Spacings.SPACING_12B};
    background-color: ${({ $primeTheme, $showCompeteBackground }) =>
      $primeTheme
        ? 'var(--color-i-prime-surface-dark)'
        : `var(${
            $showCompeteBackground ? '--color-i-violet-22' : '--color-base-1'
          })`};
    ${({ $primeTheme, $hidePrimeMwebBg }) =>
      $primeTheme && !$hidePrimeMwebBg
        ? `background-image: url('/static/images/prime/prime-background-mweb.svg');
           background-position: center top;
           background-size: 100% 142px;
           background-repeat: no-repeat;`
        : ''}
  }

  background-color: var(
    ${({ showCompeteBackground }) =>
      showCompeteBackground ? '--color-i-violet-22' : ''}
  );
`;

const HeaderParent = styled(HeaderContent)`
  height: ${Spacings.SPACING_18B};

  ${mediaQuery.uptoTablet} {
    width: 100%;
    padding: 0 ${Spacings.SPACING_4B};
  }
  ${mediaQuery.uptoMobile} {
    display: flex;
    width: 100%;
    height: ${Spacings.SPACING_12B};
    padding: ${Spacings.SPACING_4B};
    justify-content: space-between;
  }
`;

const StyledDivider = styled(Divider)`
  &.MuiDivider-root {
    margin: 0
      ${({ $primeTheme }) =>
        $primeTheme ? Spacings.SPACING_3B : Spacings.SPACING_6B};
    height: 24px;
    width: 2px;
    border-radius: ${Spacings.SPACING_2};
  }
  ${mediaQuery.uptoMobile} {
    display: none;
  }

  background-color: var(
    ${({ $showCompeteBackground }) =>
      $showCompeteBackground ? '--color-i-divider' : ''}
  );
  ${({ $primeTheme }) => ($primeTheme ? 'background-color: #4D4276;' : '')}
`;

const RightContentWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const HeaderReferralWrapper = styled(Button)`
  box-sizing: border-box;
  margin-right: ${({ $isLoggedIn }) =>
    $isLoggedIn ? Spacings.SPACING_0B : Spacings.SPACING_4B};
  padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_3B};
  height: ${({ $isLoggedIn }) =>
    $isLoggedIn ? Spacings.SPACING_8B : Spacings.SPACING_10B};
  border: ${({ $primeTheme }) =>
    $primeTheme ? 'none' : '1px solid var(--color-i-divider)'};
  border-radius: ${({ $isLoggedIn }) =>
    $isLoggedIn ? Spacings.SPACING_12B : Spacings.SPACING_1B};
  &.MuiButton-root {
    text-transform: unset;
    border-radius: 50%;
    background-color: ${({ $primeTheme }) =>
      $primeTheme ? 'var(--color-i-prime-card)' : 'transparent'};
    ${({ $isLoggedIn }) => {
      return $isLoggedIn
        ? `min-width:${Spacings.SPACING_8B}; width:${Spacings.SPACING_8B}; height:${Spacings.SPACING_8B};`
        : `min-width:${Spacings.SPACING_10B}; width:${Spacings.SPACING_10B}; height:${Spacings.SPACING_10B};`;
    }}
  }
  ${mediaQuery.uptoMobile} {
    margin-right: ${({ $isLoggedIn }) =>
      $isLoggedIn ? Spacings.SPACING_0B : Spacings.SPACING_2B};
    &.MuiButton-root {
      min-width: 32px;
      height: 32px;
      width: 32px;
    }
  }
`;

const ReferralContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
  flex: none;
`;

const HeaderUserContainer = styled.div`
  min-width: ${({ $hideWatchMinForPlatform }) =>
    $hideWatchMinForPlatform ? 'unset' : '185px'};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const GoalSelectorWrapper = styled.div`
  display: flex;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    flex: 1;
    margin-right: ${Spacings.SPACING_2B};
    button {
      padding-left: 0px;
    }
  }
`;
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  ${({ $primeTheme }) =>
    $primeTheme
      ? `
        ${mediaQuery.aboveTablet} {
          img {
            width: 130px;
            height: auto;
          }
        }
      `
      : ''}
  ${mediaQuery.uptoMobile} {
    margin-right: ${({ $primeTheme }) =>
      $primeTheme ? Spacings.SPACING_3B : Spacings.SPACING_0B};
    ${({ $primeTheme }) =>
      $primeTheme
        ? `
        img,
        svg {
          width: 96px;
          height: auto;
        }
      `
        : `
        img,
        svg {
          width: initial;
          height: initial;
        }
      `}
  }
`;

const UpscCurrentAffairsHeaderTitle = styled.span`
  font-size: ${Fonts.MEDIUM};
  font-weight: 600;
  line-height: 24px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
    line-height: 20px;
  }
`;

const isSubscriptionPricingsUrl = (route) =>
  route === '/goal/[goalSlug]/[goalUID]/subscribe';

const onlyLogo = ['/careers/public-notice'];

const hideLoginButtonOnMobileUrls = [
  '/goal/[goalSlug]/[goalUID]',
  '/course/[slug]/[courseUID]',
  '/lesson/[lessonSlug]/[lessonUID]'
];

const Header = ({
  transparent,
  hideSearch,
  hideGoalSelector,
  isCheckoutUrl,
  isLoginUrl,
  primeTheme,
  hidePrimeMwebBg
}) => {
  const isEducatorSide = useEducatorContext();
  const router = useRouter();
  const { route, query } = router;
  const isNotificationsRoute = route === '/notifications';
  const isUpscCurrentAffairsPage = isUpscCurrentAffairsRoute(route);
  const [mounted, setMounted] = useState(false);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const selectedGoalUID = useSelectedGoalUID();
  const { selectedGoal } = useSelectedGoal();
  const showOnlyLogo = onlyLogo.includes(route);
  const { isMobile } = useWindowSize();
  useTimeout(() => setMounted(true), 0);

  let hideGoalSelectorInHeader = useFlagSelector('hideGoalSelector');
  hideGoalSelectorInHeader = hideGoalSelector || hideGoalSelectorInHeader;
  const { isPlatform, hideWatchMinForPlatform } = usePlatformInfo();

  const isProdigyUrl =
    route === '/prodigy' ||
    route === '/event/UCL' ||
    route === '/event/testseries/aspire' ||
    route === '/event/testseries/[testSeriesKey]';

  const isGoalOnBoardingPreferenceUrl =
    route === '/goal/[goalSlug]/[goalUID]/preferences';

  const isGTP = useConstants(getIsGTPGoal(selectedGoalUID));

  const displayNotification =
    isLoggedIn &&
    mounted &&
    !isNotificationsRoute &&
    !isEducatorSide &&
    !isCheckoutUrl &&
    !isProdigyUrl &&
    !isGoalOnBoardingPreferenceUrl &&
    !isGTP;

  const displayGoalSelector =
    !isCheckoutUrl &&
    !hideGoalSelectorInHeader &&
    !isEducatorSide &&
    !(isMobile && isLoginUrl) &&
    !isGoalOnBoardingPreferenceUrl &&
    !isUpscCurrentAffairsPage;

  const displaySearch =
    !isPlatform &&
    !hideSearch &&
    !isCheckoutUrl &&
    !isEducatorSide &&
    mounted &&
    !(isMobile && isLoginUrl) &&
    !isGoalOnBoardingPreferenceUrl &&
    !isGTP;

  const selectedCategoryUID = usePrimeHomeStore(
    (state) => state.selectedCategoryUID
  );

  const { showCompeteBackground, state } = useCompeteConfig();
  const goalSlug = Array.isArray(query.goalSlug)
    ? query.goalSlug[0]
    : query.goalSlug;
  const routeGoalUID = Array.isArray(query.goalUID)
    ? query.goalUID[0]
    : query.goalUID;
  const routeCategoryUID = Array.isArray(query.categoryUID)
    ? query.categoryUID[0]
    : query.categoryUID;
  const activeGoalUID = routeGoalUID || selectedGoalUID;
  const activeGoalSlug = goalSlug || selectedGoal?.slug;
  const activeCategoryUID = selectedCategoryUID || routeCategoryUID;
  const primeSearchHref =
    primeTheme && activeGoalSlug && activeGoalUID && activeCategoryUID
      ? `/goal/${activeGoalSlug}/${activeGoalUID}/prime/search/${activeCategoryUID}`
      : '';

  const isCompetePostLaunch = state === COMPETE_STATES.POST_LAUNCH;
  const handleReferralRedirect = () => {
    segment.track(REFERRAL_TOUCHPOINT_CLICKED, {
      last_primary_source_section: REFERRAL_TOPBAR_TOUCHPOINT
    });
    const path = isLoggedIn ? '/settings/referral' : '/referral';
    pushRoute(path, path);
  };

  const renderDesktopHeaderAuthActions = () => {
    if (isLoggedIn) {
      return (
        <HeaderUserContainer $hideWatchMinForPlatform={hideWatchMinForPlatform}>
          <HeaderUser
            isEducatorUrl={isEducatorSide}
            isHeaderTransparent={transparent}
            primeTheme={primeTheme}
          />
        </HeaderUserContainer>
      );
    }
    if (isUpscCurrentAffairsPage) {
      return <UpscCurrentAffairsActions />;
    }
    return <RenderLoginAction isJoinForFreeCtaExperiment />;
  };

  const renderMobileHeaderAuthActions = () => {
    if (isUpscCurrentAffairsPage) {
      return <UpscCurrentAffairsActions />;
    }
    return (
      <RenderLoginAction
        show={!hideLoginButtonOnMobileUrls.includes(route)}
        isJoinForFreeCtaExperiment
      />
    );
  };

  const isMWUrl = route === '/login' || route === '/about';
  if (isMobile && isMWUrl) {
    return (
      <HeaderContainer transparent={transparent}>
        <HeaderParent>
          <Logo isReload />
        </HeaderParent>
      </HeaderContainer>
    );
  }

  return (
    <HeaderContainer
      transparent={transparent}
      hideBoxShadow={isGoalOnBoardingPreferenceUrl}
      $showCompeteBackground={showCompeteBackground}
      $isCompetePostLaunch={isCompetePostLaunch}
      $primeTheme={primeTheme}
      $hidePrimeMwebBg={hidePrimeMwebBg}
    >
      <CompeteHeaderStrip />
      <HeaderParent>
        <LogoContainer $primeTheme={primeTheme}>
          {isCheckoutUrl && !isSubscriptionPricingsUrl(route) ? (
            <Logo isClassroom={route.includes('classroom')} disabled />
          ) : (
            <Logo
              isClassroom={route.includes('classroom')}
              isReload={(isProdigyUrl || isLoginUrl) && isMobile}
              isPrime={primeTheme}
            />
          )}
        </LogoContainer>
        {!showOnlyLogo && (
          <>
            {isUpscCurrentAffairsPage && (
              <GoalSelectorWrapper>
                <StyledDivider
                  orientation="vertical"
                  $showCompeteBackground={showCompeteBackground}
                />
                <UpscCurrentAffairsHeaderTitle>
                  {UPSC_CURRENT_AFFAIRS_HEADER_TITLE}
                </UpscCurrentAffairsHeaderTitle>
              </GoalSelectorWrapper>
            )}
            {displayGoalSelector && (
              <GoalSelectorWrapper>
                <StyledDivider
                  orientation="vertical"
                  $showCompeteBackground={showCompeteBackground}
                  $primeTheme={primeTheme}
                />
                <GoalSelector primeTheme={primeTheme} />
              </GoalSelectorWrapper>
            )}
            {isLoggedIn && isMobile && (
              <ProfileOptions
                excludeOptions={['darkMode']}
                primeTheme={primeTheme}
              />
            )}
            <RightContentWrapper>
              {displaySearch && <SearchTab primeSearchHref={primeSearchHref} />}
              {displayNotification && (
                <HeaderNotification primeTheme={primeTheme} />
              )}
              <PaymentNudge />
              <NotificationToPwa />
              {isEducatorSide && <AutoDppDismissibleCard />}
              {!isCheckoutUrl && !isLoginUrl && (
                <>
                  <HeaderReferralWrapper
                    onClick={handleReferralRedirect}
                    $isLoggedIn={isLoggedIn}
                    $primeTheme={primeTheme}
                  >
                    <ReferralContainer>
                      <Image
                        src="giftHomePage.svg"
                        height="18px"
                        width="18px"
                        alt="avatar"
                        loading="eager"
                        priority
                      />
                    </ReferralContainer>
                  </HeaderReferralWrapper>
                  {renderDesktopHeaderAuthActions()}
                </>
              )}
            </RightContentWrapper>

            {isMobile &&
              !isLoggedIn &&
              !isCheckoutUrl &&
              !isLoginUrl &&
              renderMobileHeaderAuthActions()}
          </>
        )}
      </HeaderParent>
    </HeaderContainer>
  );
};

export default Header;

Header.propTypes = {
  isCheckoutUrl: PropTypes.bool,
  hideSearch: PropTypes.bool,
  hideGoalSelector: PropTypes.bool,
  transparent: PropTypes.bool,
  isLoginUrl: PropTypes.bool,
  primeTheme: PropTypes.bool
};

Header.defaultProps = {
  isCheckoutUrl: false,
  hideSearch: false,
  hideGoalSelector: false,
  transparent: false,
  isLoginUrl: false,
  primeTheme: false
};
